import React, { Fragment } from 'react';
import { arrayOf, bool, node, object, shape, string } from 'prop-types';
import NamedLink from '../NamedLink/NamedLink';
import css from './Breadcrumbs.module.css';

/**
 * Shared breadcrumb bar.
 *
 * The outer bar always spans the full viewport width (matches the topbar).
 * The trail content is capped at --contentMaxWidth (1400px) with the
 * platform's standard 20/32px gutter — same cap rule as the topbar inner,
 * landing sections, and footer.
 *
 * Pass `trail` as an array of crumbs. Each crumb is either:
 *   - { label, name, params?, to? } → rendered as a NamedLink
 *   - { label, href }               → rendered as a plain <a>
 *   - { label }                     → rendered as the current/terminal item
 * The last crumb is always rendered as non-link text (the current page).
 */
const ChevronRight = () => (
  <svg
    className={css.sep}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

const Breadcrumbs = ({ trail, children, rightSlot, singleLineOnMobile }) => {
  if (!trail || trail.length === 0) return null;

  const hasAside = !!rightSlot;
  const innerClass = hasAside
    ? `${css.innerStacked} ${css.innerWithAside}`
    : children
      ? css.innerStacked
      : css.inner;

  const mainBlock = (
    <>
      <ol className={singleLineOnMobile ? `${css.trail} ${css.trailSingleLine}` : css.trail}>
        {trail.map((crumb, idx) => {
          const isLast = idx === trail.length - 1;
          return (
            <Fragment key={`${crumb.label}-${idx}`}>
              {idx > 0 ? <ChevronRight /> : null}
              {isLast || (!crumb.name && !crumb.href) ? (
                <li className={css.current}>{crumb.label}</li>
              ) : crumb.name ? (
                <li>
                  <NamedLink
                    name={crumb.name}
                    params={crumb.params}
                    to={crumb.to}
                    className={css.link}
                  >
                    {crumb.label}
                  </NamedLink>
                </li>
              ) : (
                <li>
                  <a href={crumb.href} className={css.link}>
                    {crumb.label}
                  </a>
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
      {children}
    </>
  );

  return (
    <nav className={css.root} aria-label="Breadcrumb">
      <div className={innerClass}>
        {hasAside ? (
          <>
            <div className={css.mainBlock}>{mainBlock}</div>
            <div className={css.aside}>{rightSlot}</div>
          </>
        ) : (
          mainBlock
        )}
      </div>
    </nav>
  );
};

Breadcrumbs.propTypes = {
  /* One scrollable line on phones instead of wrapping (P25009-482 AC-5: the
   * listing page needs its first screen for the buy card). */
  singleLineOnMobile: bool,
  trail: arrayOf(
    shape({
      label: node.isRequired,
      name: string,
      params: object,
      to: object,
      href: string,
    })
  ).isRequired,
  children: node,
  rightSlot: node,
};

export default Breadcrumbs;
